import * as React from 'react';
import { useEffect, useState } from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { CenterBox } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import { useCourseStoresLoading } from 'bundles/course-v2/hooks/useCourseStoresLoading';
import { useLoadComputedModels } from 'bundles/course-v2/hooks/useLoadComputedModels';
import { useLoadCourseDeadlines } from 'bundles/course-v2/hooks/useLoadCourseDeadlines';
import { useLoadCourseSessionGroup } from 'bundles/course-v2/hooks/useLoadCourseSessionGroup';
import { useLoadCourseStores } from 'bundles/course-v2/hooks/useLoadCourseStores';
import { useLoadVerificationDisplay } from 'bundles/course-v2/hooks/useLoadVerificationDisplay';
import { useCustomLabelsByUserAndCourse } from 'bundles/custom-labels/hoc/withCustomLabelsByUserAndCourse';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';

const CourseDataProvider = ({ children }: React.PropsWithChildren) => {
  useLoadComputedModels();
  useLoadVerificationDisplay();
  useLoadCourseDeadlines();
  useLoadCourseSessionGroup();
  useLoadCourseStores();

  const { loading } = useCourseStoresLoading();
  if (loading) {
    return null;
  }

  return children;
};

const CourseProvider = ({ children }: React.PropsWithChildren) => {
  const { loading } = useCourseStoresLoading();

  const { courseId } = useFluxibleStore(CourseStore, (store) => ({ courseId: store.getCourseId() }));
  useCustomLabelsByUserAndCourse({ courseId });

  return (
    <div data-testid="course-provider">
      <CourseDataProvider>{children}</CourseDataProvider>
      {loading && (
        <CenterBox height={512}>
          <SvgLoaderSignal />
        </CenterBox>
      )}
    </div>
  );
};

const DeferToClientSideRender = ({ children }: React.PropsWithChildren) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return <CourseProvider>{children}</CourseProvider>;
};

export default DeferToClientSideRender;
